import './InputField.scss';
import React, { Component } from 'react';
import classNames from 'classnames';
import Loader from '../loader/Loader';
import { RESPONSE } from '../../js/_constants';

type SuggestionValue = string | number;
type InputValue = string | number | readonly string[] | '';

type InputFieldSuggestion = {
    label: string;
    prefix?: string;
    secondaryLabel?: string;
    value: SuggestionValue;
};

type InputFieldProps = {
    name: string;
    inputType?: string;
    value?: InputValue;
    initialValue?: InputValue;
    suggestions?: InputFieldSuggestion[];
    maxAutocomplete?: number;
    strictSuggestions?: boolean;
    isLoadingSuggestions?: boolean;
    onSubmitHandler?: (value?: string) => void;
    onChangeHandler?: (e: React.ChangeEvent<any>) => void;
    onValueChangeHandler?: (value: string) => void;
    onClearHandler?: () => void;
    onKeyDown?: (e: React.KeyboardEvent<any>) => void;
    onFocusHandler?: (e: React.FocusEvent<any>) => void;
    onBlurHandler?: (e: React.FocusEvent<any>) => void;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    hint?: string;
    minDate?: string | null;
    withFilter?: boolean;
    filterList?: (value: string | SuggestionValue) => void;
    isFocusDisabled?: boolean;
    extraClass?: string;
    clearIcon?: boolean;
    hideLabel?: boolean;
    reference?: React.Ref<HTMLInputElement>;
    isFocused?: boolean;
    autoFocus?: boolean;
    rightIcon?: React.ReactNode;
    isActive?: boolean;
    isValid?: boolean;
    isValidating?: boolean;
};

type InputFieldState = {
    hasFocus: boolean;
    isHovered: boolean;
    isDisabled: boolean;
    value: InputValue;
    isOpen: boolean;
    highlightedIndex: number;
};

class InputField extends Component<InputFieldProps, InputFieldState> {
    constructor(props: InputFieldProps) {
        super(props);

        this.state = {
            hasFocus: false,
            isHovered: false,
            isDisabled: !!props.disabled,
            value: props.value ?? props.initialValue ?? '',
            isOpen: false,
            highlightedIndex: -1,
        };
    }

    componentDidUpdate(prevProps: InputFieldProps) {
        if (prevProps.value !== this.props.value) {
            this.setState({ value: this.props.value ?? '' });
        }

        if (prevProps.disabled !== this.props.disabled) {
            this.setState({ isDisabled: !!this.props.disabled });
        }
    }

    onFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (this.props.onFocusHandler) {
            this.props.onFocusHandler(e);
        }

        this.setState({
            hasFocus: true,
            isOpen: this.props.inputType === 'autocomplete',
        });
    };

    onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (this.props.onBlurHandler) {
            this.props.onBlurHandler(e);
        }

        setTimeout(() => {
            this.setState({
                hasFocus: false,
                isOpen: false,
                highlightedIndex: -1,
            });
        }, 150);
    };

    onValueChange = (value: string) => {
        if (this.props.onValueChangeHandler) {
            this.props.onValueChangeHandler(value);
        }

        this.setState({ value });

        if (this.props.withFilter && this.props.filterList) {
            this.props.filterList(value);
        }
    };

    onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.currentTarget.value;

        if (this.props.onChangeHandler) {
            this.props.onChangeHandler(e);
        }

        this.onValueChange(value);

        if (this.props.inputType === 'autocomplete') {
            this.setState({ isOpen: true, highlightedIndex: -1 });
        }
    };

    onSuggestionSelect = (value: SuggestionValue) => {
        const { strictSuggestions = false, onSubmitHandler } = this.props;

        const finalValue = strictSuggestions
            ? `"${value}"`
            : String(value);

        if (onSubmitHandler) {
            onSubmitHandler(finalValue);
        } else {
            this.onValueChange(finalValue);
        }

        this.setState({
            isOpen: false,
            highlightedIndex: -1,
        });
    };

    cleanInput = () => {
        if (this.props.onClearHandler) {
            this.props.onClearHandler();
        }
        this.onValueChange('');
    };

    getFilteredSuggestions = () => {
        const { suggestions = [] } = this.props;
        const { value } = this.state;

        if (!value) return suggestions;

        const normalizedValue = String(value).toLowerCase();

        return suggestions.filter(item =>
            item.label.toLowerCase().includes(normalizedValue)
        );
    };

    onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const items = this.getFilteredSuggestions();

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.setState(prev => ({
                    highlightedIndex:
                        items.length === 0
                            ? -1
                            : (prev.highlightedIndex + 1) % items.length,
                }));
                break;

            case 'ArrowUp':
                e.preventDefault();
                this.setState(prev => ({
                    highlightedIndex:
                        items.length === 0
                            ? -1
                            : (prev.highlightedIndex - 1 + items.length) %
                              items.length,
                }));
                break;

            case 'Enter':
                if (this.state.highlightedIndex >= 0) {
                    const selected = items[this.state.highlightedIndex];
                    if (selected) {
                        this.onSuggestionSelect(selected.value);
                    }
                }
                break;

            case 'Escape':
                this.setState({ isOpen: false });
                break;

            default:
                if (this.props.onKeyDown) {
                    this.props.onKeyDown(e);
                }
        }
    };

    renderAutocompleteMenu() {
        const { isLoadingSuggestions, maxAutocomplete } = this.props;
        const { isOpen, highlightedIndex } = this.state;

        if (!isOpen) return null;

        let items = this.getFilteredSuggestions();

        if (maxAutocomplete && items.length > maxAutocomplete) {
            items = items.slice(0, maxAutocomplete);
        }

        if (isLoadingSuggestions) {
            return (
                <div className="b-autocomplete-menu">
                    <Loader loading isSmall />
                </div>
            );
        }

        return (
            <div role="listbox" className="b-autocomplete-menu">
                {items.map((item, index) => {
                    const classes = classNames('b-autocomplete-item', {
                        'is-highlighted': index === highlightedIndex,
                    });

                    let key: string | number = item.value;
                    if (typeof item.value === 'string') {
                        try {
                            const parsed = JSON.parse(item.value);
                            key = parsed[RESPONSE.ID] ?? item.value;
                        } catch (error) {
                            console.warn('Failed to parse suggestion value, using raw value as key', error);
                        }
                    }

                    return (
                        <div
                            key={key}
                            role="option"
                            aria-selected={index === highlightedIndex}
                            className={classes}
                            onMouseDown={() =>
                                this.onSuggestionSelect(item.value)
                            }
                        >
                            {item.prefix && (
                                <span className="primary-label">
                                    {item.prefix} {item.label}
                                </span>
                            )}
                            <p className="secondary-label">
                                {item.secondaryLabel}
                            </p>
                        </div>
                    );
                })}
            </div>
        );
    }

    getClasses() {
        const {
            isFocusDisabled = false,
            extraClass,
            hint = '',
            inputType,
            isValidating,
            isValid,
        } = this.props;
        const { hasFocus, isDisabled, value } = this.state;

        const classes: Record<string, boolean| undefined> = {
            'b-input-field': true,
            'is-disabled': isDisabled,
            'is-focused': !isFocusDisabled && hasFocus,
            'is-dirty': !!value || inputType === 'date',
            'is-with-hint': !!hint,
            'is-invalid': isValidating && !isValid,
        };

        if (extraClass) {
            classes[extraClass] = true;
        }

        return classes;
    }

    renderInput() {
        const {
            inputType,
            name,
            label,
            minDate = null,
            placeholder,
            reference,
            autoFocus,
        } = this.props;
        const { value, isDisabled } = this.state;

        const commonProps = {
            className: classNames({ 'disabled': isDisabled }),
            name,
            id: name,
            "data-testid": `input-field-${name}`,
            value,
            disabled: isDisabled,
            placeholder,
            'aria-label': label,
            onChange: this.onInputChange,
            onKeyDown: this.onKeyDown,
            onFocus: this.onFocus,
            onBlur: this.onBlur,
            autoFocus,
        };

        if (inputType === 'autocomplete') {
            return (
                <div className="b-autocomplete-wrapper">
                    <input {...commonProps} ref={reference} type="text" />
                    {this.renderAutocompleteMenu()}
                </div>
            );
        }

        if (inputType === 'textarea') {
            return <textarea {...commonProps} />;
        }

        return <input {...commonProps} ref={reference} type={inputType} min={inputType === 'date' ? minDate ?? undefined : undefined} />;
    }

    render() {
        const { label, clearIcon = false, hint = '', rightIcon } = this.props;
        const { value } = this.state;

        const containerClasses = classNames(this.getClasses());

        const cancelIconClasses = classNames('cancel-icon', {
            'is-visible': clearIcon && value,
        });

        return (
            <div className={containerClasses}>
                <div className="item-field-container">
                    <div className="input-wrapper">
                        {this.renderInput()}
                        <span
                            role="button"
                            tabIndex={0}
                            className={cancelIconClasses}
                            onClick={this.cleanInput}
                        />
                        {rightIcon}
                    </div>

                    {label && <label htmlFor={this.props.name}>{label}</label>}
                    <span className="focus-line" />
                    {hint && <span className="hint-text">{hint}</span>}
                </div>
            </div>
        );
    }
}

export default InputField;